import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Rocket, GraduationCap, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabsJoinPage() {
  const { code: codeParam } = useParams();
  const [code, setCode] = useState(codeParam || '');
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState('create'); // create | login
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [form, setForm] = useState({ first_name: '', username: '', password: '', age: '', grade: '', role: 'student' });

  // A join link printed on a handout must never demo old code. Ask which build
  // is newest and forward the participant there before they sign up.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('b') === '1') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/labs/build`, { timeout: 8000 });
        const target = (data?.latest?.url || '').replace(/\/+$/, '');
        const here = window.location.origin.replace(/\/+$/, '');
        if (!cancelled && target && target !== here) {
          setRedirecting(true);
          window.location.replace(`${target}${window.location.pathname}?b=1`);
        }
      } catch {
        /* stay on this build rather than blocking the participant */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const lookup = async (c) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/labs/join/${encodeURIComponent(c)}`);
      if (!data.open) { toast.error(data.message || 'This lab is not available.'); setSession(null); }
      else { setSession(data); localStorage.setItem('futurepath_labs_code', c); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't find that lab.");
      setSession(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (codeParam) lookup(codeParam); /* eslint-disable-next-line */ }, [codeParam]);

  const enterApp = (token, role, onboardingComplete) => {
    localStorage.setItem('futurepath_token', token);
    if (role === 'parent') window.location.href = '/app/family';
    else window.location.href = onboardingComplete ? '/app/dashboard' : '/app/welcome';
  };

  const submitCreate = async () => {
    if (!form.username || !form.password) return toast.error('Choose a username and password.');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/labs/join/${encodeURIComponent(code)}/register`, form);
      toast.success('Welcome to Agentic FuturePath AI!');
      enterApp(data.access_token, data.role || form.role, data.user?.onboarding_complete);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create your account.'); }
    finally { setLoading(false); }
  };

  const submitLogin = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/labs/login`, { username: form.username, password: form.password });
      enterApp(data.access_token, data.user?.role, data.user?.onboarding_complete);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid username or password.'); }
    finally { setLoading(false); }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-400/60 focus:outline-none transition';

  const RoleCard = ({ value, icon: Icon, title, subtitle }) => (
    <button
      type="button"
      onClick={() => setForm({ ...form, role: value })}
      data-testid={`labs-role-${value}`}
      className={`flex-1 text-left p-4 rounded-2xl border transition ${
        form.role === value
          ? 'border-cyan-400/70 bg-cyan-500/15 shadow-[0_0_0_1px_rgba(34,211,238,0.4)]'
          : 'border-slate-700 bg-slate-900/50 hover:border-slate-500'
      }`}
    >
      <Icon className={`w-6 h-6 mb-2 ${form.role === value ? 'text-cyan-300' : 'text-slate-300'}`} />
      <p className="text-white font-semibold text-sm">{title}</p>
      <p className="text-slate-300 text-xs mt-0.5">{subtitle}</p>
    </button>
  );

  return (
    <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-4 relative overflow-hidden">
      {redirecting && (
        <div className="absolute inset-0 z-10 bg-[#0a1729]/90 flex flex-col items-center justify-center gap-3" data-testid="labs-build-redirect">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
          <p className="text-sm text-slate-300">Taking you to the latest version of FuturePath…</p>
        </div>
      )}
      <div className="relative w-full max-w-md">
        {/* Brand header, title on one line */}
        <div className="flex items-center gap-3 justify-center mb-6">
          <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="w-11 h-11 rounded-2xl" />
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white whitespace-nowrap tracking-tight">
            Agentic FuturePath AI Labs
          </h2>
        </div>

        <div className="glass-card p-6" data-testid="labs-join-card">
          {!session ? (
            <>
              <h1 className="font-outfit text-2xl font-bold text-white text-center">Enter your Lab Code</h1>
              <p className="text-slate-300 text-sm text-center mt-1 mb-5">Your facilitator gave you a code to join.</p>
              <input className={inputCls} placeholder="e.g. FUTURE26" value={code} onChange={(e) => setCode(e.target.value)} data-testid="labs-code-input" />
              <button onClick={() => code && lookup(code)} disabled={loading} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold active:scale-95 transition disabled:opacity-60" data-testid="labs-code-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            </>
          ) : (
            <>
              {/* Large circular Demo Mode badge */}
              <div className="flex flex-col items-center text-center mb-5">
                <div
                  className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex flex-col items-center justify-center shadow-lg shadow-blue-900/40 ring-4 ring-blue-400/20"
                  data-testid="labs-demo-badge"
                >
                  <span className="text-white font-outfit font-bold text-base leading-none">Demo</span>
                  <span className="text-white font-outfit font-bold text-base leading-tight">Mode</span>
                </div>
                <h1 className="font-outfit text-lg font-bold text-white mt-3">{session.name}</h1>
              </div>

              <div className="flex rounded-xl bg-slate-900/60 p-1 mb-4 text-sm">
                <button onClick={() => setMode('create')} className={`flex-1 py-2 rounded-lg transition ${mode === 'create' ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-300'}`} data-testid="labs-tab-create">Create account</button>
                <button onClick={() => setMode('login')} className={`flex-1 py-2 rounded-lg transition ${mode === 'login' ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-300'}`} data-testid="labs-tab-login">I have an account</button>
              </div>

              {mode === 'create' ? (
                <div className="space-y-3">
                  {/* Role selection */}
                  <p className="text-slate-300 text-sm font-medium">I want to explore as a…</p>
                  <div className="flex gap-3">
                    <RoleCard value="student" icon={GraduationCap} title="Student" subtitle="Discover your future path" />
                    <RoleCard value="parent" icon={Users} title="Parent" subtitle="Guide your child's journey" />
                  </div>

                  <input className={inputCls} placeholder="First name or nickname" autoComplete="off" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="labs-firstname" />
                  <input className={inputCls} placeholder="Username" name="labs_username" autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="labs-username" />
                  <input className={inputCls} type="password" placeholder="Password" name="labs_new_password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="labs-password" />
                  {form.role === 'student' && (
                    <div className="grid grid-cols-2 gap-3">
                      <input className={inputCls} placeholder="Age" autoComplete="off" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="labs-age" />
                      <input className={inputCls} placeholder="Grade" autoComplete="off" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} data-testid="labs-grade" />
                    </div>
                  )}
                  <button onClick={submitCreate} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold active:scale-95 transition disabled:opacity-60" data-testid="labs-create-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4" /> Create My FuturePath</>}
                  </button>
                  <p className="text-xs text-slate-300 text-center leading-relaxed" data-testid="labs-minor-disclaimer">
                    No email or payment required, this is a demo environment. If you're under 18,
                    please explore with a parent or guardian and don't enter real personal details.
                  </p>
                  <p className="text-[11px] text-cyan-300/80 text-center">
                    Tip: you can switch between the Parent and Student views anytime inside the demo.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input className={inputCls} placeholder="Username" name="labs_login_username" autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="labs-login-username" />
                  <input className={inputCls} type="password" placeholder="Password" name="labs_login_password" autoComplete="off" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="labs-login-password" />
                  <button onClick={submitLogin} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold active:scale-95 transition disabled:opacity-60" data-testid="labs-login-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Log in <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-center text-slate-300 text-xs mt-6">Powered by Agentic FuturePath AI</p>
      </div>
    </div>
  );
}
